import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { IconButton } from '@/components/core/IconButton';
import { Input } from '@/components/core/Input';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { ForceAttributeEditableProvider } from '@/components/datamodel/hooks/useIsAttributeReadonly';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useDataModel } from '@/hooks/useDataModel';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector } from '@/services/angularjs-bridge';
import { DataModel, DataModelAttribute } from '@/types/DataModel';

import { AttributePicker } from './AttributePicker';
import { FilterAttributeOption, FilterKeyword, FilterRule } from './FilterBuilder.types';
import {
    getAttributeName,
    isAllowedFilterWidget,
    prepareFilterKeyword,
    resolveRuleDataModelAttribute,
} from './filterGroups';
import { OperatorSelect } from './OperatorSelect';

interface RuleRowProps {
    rule: FilterRule;
    attributes: FilterAttributeOption[];
    onChange: (rule: FilterRule) => void;
    onRemove: () => void;
    onAdd: () => void;
}

function canUseAttributeComponent(): boolean {
    try {
        return typeof getInjector()?.get === 'function';
    } catch {
        return false;
    }
}

function resolveFilterLayoutName(dataModel?: DataModel): string {
    if (dataModel?.hasLayout?.('filter')) {
        return 'filter';
    }
    if (dataModel?.hasLayout?.('browse')) {
        return 'browse';
    }
    return 'edit';
}

function isEmptyKeyword(keyword: FilterKeyword): boolean {
    if (keyword instanceof Date) {
        return false;
    }
    if (Array.isArray(keyword)) {
        return keyword.length === 0;
    }
    return keyword === '' || keyword == null;
}

function keywordsEqual(left: FilterKeyword, right: FilterKeyword): boolean {
    if (left instanceof Date && right instanceof Date) {
        return left.getTime() === right.getTime();
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        return left.length === right.length && left.every((value, index) => value === right[index]);
    }
    return left === right;
}

const RuleValueField = ({
    attribute,
    keyword,
    onKeywordChange,
}: {
    attribute: DataModelAttribute | null;
    keyword: FilterKeyword;
    onKeywordChange: (keyword: FilterKeyword) => void;
}) => {
    const { dataModel } = useDataModel();
    const layoutName = resolveFilterLayoutName(dataModel);
    const editorAttribute = useMemo(() => {
        if (!attribute) {
            return null;
        }
        return {
            ...attribute,
            readonly: false,
            params: {
                ...attribute.params,
                uiShowLength: false,
            },
        };
    }, [attribute]);

    const methods = useForm({
        defaultValues: editorAttribute ? { [editorAttribute.name]: keyword } : {},
    });

    useEffect(() => {
        if (editorAttribute) {
            methods.reset({ [editorAttribute.name]: keyword });
        }
    }, [editorAttribute, methods, keyword]);

    useEffect(() => {
        if (!editorAttribute) {
            return;
        }
        const subscription = methods.watch((values) => {
            const next = values[editorAttribute.name] as FilterKeyword;
            if (!keywordsEqual(next ?? '', keyword)) {
                onKeywordChange(next ?? '');
            }
        });
        return () => subscription.unsubscribe();
    }, [editorAttribute, keyword, methods, onKeywordChange]);

    if (editorAttribute && isAllowedFilterWidget(editorAttribute) && canUseAttributeComponent()) {
        return (
            <FormResetProvider>
                <ForceAttributeEditableProvider value={true}>
                    <FormProvider {...methods}>
                        <LayoutProvider layoutName={layoutName}>
                            <div className="tw-flex-1 tw-min-w-0 tw-flex tw-items-center
                                [&_.clear-button-container]:tw-w-full [&_.clear-button-container]:tw-min-w-0
                                [&_.clear-button-container_.tw-absolute]:!tw-hidden">
                                <AttributeComponent
                                    attribute={editorAttribute}
                                    hideLabel={true}
                                    disableUrlSync={true}
                                    fieldPath={editorAttribute.name}
                                    onBlur={() => onKeywordChange(methods.getValues(editorAttribute.name) as FilterKeyword)}
                                />
                            </div>
                        </LayoutProvider>
                    </FormProvider>
                </ForceAttributeEditableProvider>
            </FormResetProvider>
        );
    }

    return (
        <Input
            id="filter-rule-value"
            className="tw-flex-1 tw-min-w-0 tw-h-9 lg:tw-h-10"
            value={typeof keyword === 'string' ? keyword : ''}
            onChange={(event) => onKeywordChange(event.target.value)}
        />
    );
};

const RuleRow = ({ rule, attributes, onChange, onRemove, onAdd }: RuleRowProps) => {
    const { t } = useTranslation();
    const { dataModel } = useDataModel();
    const selectedName = getAttributeName(rule.attribute);
    const selectedAttribute = attributes.find((attribute) => attribute.name === selectedName) ?? rule.attribute;
    const resolvedAttribute = resolveRuleDataModelAttribute(rule, attributes, dataModel);
    const displayKeyword = prepareFilterKeyword(resolvedAttribute ?? selectedAttribute, rule.keyword);
    const [isValueFocused, setIsValueFocused] = useState(false);

    return (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-items-stretch tw-w-full tw-min-w-0
            lg:tw-flex-row lg:tw-flex-wrap lg:tw-items-center lg:tw-gap-5">
            <label className="tw-flex tw-gap-2 tw-items-center tw-shrink-0 tw-m-0">
                <Checkbox
                    isChecked={rule.notOperator}
                    onCheckedChange={(checked) => onChange({ ...rule, notOperator: checked === true })}
                    aria-label={t('ITEMS_SEARCH_FILTER.NOT')}
                />
                <span className="tw-text-t-sm lg:tw-text-t-base tw-text-t-primary tw-whitespace-nowrap">{t('ITEMS_SEARCH_FILTER.NOT')}</span>
            </label>
            <div className="tw-flex tw-gap-3 lg:tw-gap-5 tw-items-center tw-w-full tw-min-w-0 lg:tw-w-auto lg:tw-flex-1 lg:tw-min-w-[16rem]">
                <AttributePicker
                    attributes={attributes}
                    value={selectedAttribute}
                    onChange={(attribute) => {
                        const resolved = attribute
                            ? dataModel?.attribute?.(attribute.name) ?? attribute
                            : attribute;
                        onChange({ ...rule, attribute: resolved, keyword: '' });
                    }}
                />
                <OperatorSelect
                    value={rule.operator}
                    onChange={(operator) => onChange({ ...rule, operator })}
                />
            </div>
            <div className="tw-flex tw-gap-5 tw-items-center tw-w-full tw-min-w-0 lg:tw-w-auto lg:tw-flex-1 lg:tw-min-w-[14rem]">
                <div
                    className="tw-flex tw-gap-5 tw-items-center tw-flex-1 tw-min-w-0"
                    onFocus={() => setIsValueFocused(true)}
                    onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                            setIsValueFocused(false);
                        }
                    }}
                >
                    <div className="tw-flex-1 tw-min-w-0">
                        <RuleValueField
                            key={resolvedAttribute?.name || selectedName || 'empty'}
                            attribute={resolvedAttribute}
                            keyword={displayKeyword}
                            onKeywordChange={(keyword) => onChange({ ...rule, keyword })}
                        />
                    </div>
                    <IconButton
                        type="button"
                        size="md"
                        variant="destructive"
                        icon={XMarkIcon}
                        title={t('CLEAR')}
                        aria-label={t('CLEAR')}
                        disabled={isEmptyKeyword(displayKeyword)}
                        aria-hidden={!isValueFocused}
                        className={isValueFocused ? undefined : 'tw-invisible tw-pointer-events-none'}
                        onClick={() => onChange({ ...rule, keyword: '' })}
                    />
                </div>
                <div className="tw-flex tw-gap-3 lg:tw-gap-1 tw-items-center tw-shrink-0">
                    <ButtonPanel
                        isDisabled={false}
                        icon={TrashIcon}
                        variant="primary"
                        tooltip={t('ITEMS_SEARCH_FILTER.DELETE_RULE')}
                        aria-label={t('ITEMS_SEARCH_FILTER.DELETE_RULE')}
                        onClick={onRemove}
                    />
                    <ButtonPanel
                        isDisabled={false}
                        icon={PlusIcon}
                        variant="primary"
                        tooltip={t('ITEMS_SEARCH_FILTER.ADD_ANOTHER_ENTRY')}
                        aria-label={t('ITEMS_SEARCH_FILTER.ADD_ANOTHER_ENTRY')}
                        onClick={onAdd}
                    />
                </div>
            </div>
        </div>
    );
};

RuleRow.displayName = 'RuleRow';

export { RuleRow };
